import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Stack,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import DeleteIcon from '@/assets/svgs/garbage.svg';
import { tokens } from "@/tokens";
import { CreateProductInput } from '@/services/product-catalogue/pc-api';
import { formatHTSCode } from '@/utils';
import { validateProduct } from './validation-alerts';
import ExpandedProductForm from './expanded-product-form';

interface ProductReviewCardProps {
  product: CreateProductInput;
  index: number;
  onUpdate: (index: number, updatedProduct: CreateProductInput) => void;
  onRemove: (index: number) => void;
  requiresMetalPercentage?: boolean;
  isSelected?: boolean;
  expanded: boolean;
  onToggleExpand: (index: number) => void;
}

const ProductReviewCard: React.FC<ProductReviewCardProps> = ({
  product,
  index,
  onUpdate,
  onRemove,
  requiresMetalPercentage,
  isSelected,
  expanded,
  onToggleExpand,
}) => {
  const issues = validateProduct(product, requiresMetalPercentage);

  const hasErrors = issues.some(issue => issue.type === 'error');
  const hasWarnings = issues.some(issue => issue.type === 'warning');

  const getStatusColor = () => {
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  const getStatusIcon = () => {
    if (hasErrors) return <ErrorIcon fontSize="small" />;
    if (hasWarnings) return <WarningIcon fontSize="small" />;
    return <CheckCircleIcon fontSize="small" />;
  };

  const getStatusText = () => {
    if (hasErrors) return 'Has Errors';
    if (hasWarnings) return 'Needs Attention';
    return 'Valid';
  };

  const handleCardClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('button') ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('select') ||
        target.closest('.MuiFormControl-root')) {
      return;
    }
    onToggleExpand(index);
  };

  return (
    <Card
      sx={{
        mb: 1,
        border: isSelected ? `2px solid ${tokens.brand.accent}` : `1px solid ${tokens.border.default}`,
      }}
    >
      <CardContent
        sx={{
          pb: 1,
          '&:hover': {
            backgroundColor: tokens.bg.hover,
            cursor: 'pointer'
          }
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h6" component="div">
                Row {index + 1}
              </Typography>
              <Chip
                size="small"
                label={getStatusText()}
                color={getStatusColor() as any}
                icon={getStatusIcon()}
              />
              {requiresMetalPercentage && (
                <Tooltip title="Metal percentage required">
                  <Chip
                    size="small"
                    label="Metal Required"
                    color="warning"
                    variant="outlined"
                    icon={<FactoryIcon fontSize="small" />}
                  />
                </Tooltip>
              )}
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  SKU
                </Typography>
                <Typography variant="body1" fontWeight={tokens.fontWeight.semibold}>
                  {product.sku || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  Name
                </Typography>
                <Typography variant="body1" fontWeight={tokens.fontWeight.semibold}>
                  {product.name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  HTS Code
                </Typography>
                <Typography variant="body1" fontWeight={tokens.fontWeight.semibold}>
                  {product.htsCode ? formatHTSCode(product.htsCode) : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  Country
                </Typography>
                <Typography variant="body1" fontWeight={tokens.fontWeight.semibold}>
                  {product.countryOfOrigin || '-'}
                </Typography>
              </Grid>
            </Grid>

            {product.metalPercentage !== undefined && product.metalPercentage !== null && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  Metal Percentage
                </Typography>
                <Typography variant="body1" fontWeight={tokens.fontWeight.semibold}>
                  {product.metalPercentage}%
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={issues.length} color={getStatusColor() as any} invisible={issues.length === 0}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(index);
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Badge>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
            >
              <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </CardContent>

      {expanded && (
        <CardContent
          sx={{ pt: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ExpandedProductForm
            product={product}
            index={index}
            onUpdate={onUpdate}
            requiresMetalPercentage={requiresMetalPercentage}
            issues={issues}
          />
        </CardContent>
      )}
    </Card>
  );
};

export default ProductReviewCard;
